import { useQuery } from '@tanstack/react-query';

import { getTenant } from '@/services/tenant';
import type { TimeFormat } from '@/lib/utils';
import { SAVED_SETTINGS_STALE_MS } from './appearance-settings';

export type SocialLinks = {
  type:
    | 'instagram'
    | 'facebook'
    | 'linkedin'
    | 'twitter'
    | 'messenger'
    | 'whatsapp'
    | 'telegram'
    | 'tiktok';
  url?: string;
  order: number;
};

export type LocationConfig = {
  place_id: string | null;
  latitude: number | null;
  longitude: number | null;
  formatted_address: string | null;
  label: string | null;
  source: 'autocomplete' | 'manual' | 'text' | null;
  maps_api_key: string | null;
};

export type Tenant = {
  tenant_id: string;
  name: string;
  currency: string;
  description: string | null;
  logo: string | null;
  social_links: SocialLinks[] | null;
  slug: string;
  // Only ever a verified, serving domain — the backend never exposes a pending
  // claim here.
  custom_domain?: string | null;
  support_email?: string | null;
  // ISO 3166-1 alpha-2. Optional because an older backend sends none.
  country?: string | null;
  business_phone_country_code?: string | null;
  business_phone_number?: string | null;
  address?: string | null;
  location_config: LocationConfig | null;
  schedule_config: {
    timezone: string;
    time_format?: TimeFormat;
    use_min_notice_hours: boolean;
    min_notice_hours: number;
    use_max_days_in_advance: boolean;
    max_days_in_advance: number;
    use_modification_notice_hours: boolean;
    modification_notice_hours: number;
  };
  tracking_config: {
    meta_pixel_id: string | null;
    google_analytics_id: string | null;
    google_tag_manager_id?: string | null;
    openai_pixel_id?: string | null;
  } | null;
  waitlist_config?: WaitlistConfig | null;
  // Optional because an older backend sends neither: read `indexable` only
  // through isIndexable, which treats it as on, and an absent `booking_host`
  // as no host consolidation.
  indexable?: boolean;
  // The one address the studio's pages are served and published from, already
  // resolved by the backend from which of its domain's hosts is live. Null
  // while the studio has no domain of its own.
  booking_host?: string | null;
};

export type WaitlistMode = 'AUTO_BOOK' | 'CONFIRM' | 'CLAIM';

export type WaitlistConfig = {
  enabled: boolean;
  mode: WaitlistMode;
  size: number;
  cutoff_hours: number;
  offer_window_minutes: number;
};

// Shared with the server-side handoff — the seeded key has to be the one this
// hook reads, or the browser silently fetches the record a second time.
export const TENANT_QUERY_KEY = ['tenant'];

// Polling is off for every caller but the site gate, which passes an interval
// while the studio is dark. Deciding it here would mean subscribing all sixteen
// callers to the site-failure store to re-render one of them.
export default function useTenant(refetchInterval: number | false = false) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: TENANT_QUERY_KEY,
    queryFn: () => getTenant(),
    refetchInterval,
    staleTime: SAVED_SETTINGS_STALE_MS,
    refetchOnWindowFocus: true,
    // The one read that speaks for the studio rather than for a screen, so it
    // is the only one allowed to retract a site-wide failure. Read by the
    // cache-level handler in lib/query-client.ts.
    meta: { isTenantProbe: true },
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
}

// Narrowed like the backend's resolveTimeFormat: the column is JSONB and the
// public payload passes it through unchecked.
export function useTimeFormat(): TimeFormat {
  const { data } = useTenant();
  return data?.schedule_config?.time_format === '12h' ? '12h' : '24h';
}
